// ─────────────────────────────────────────────────────────────────────
// WHICH CLUB LISTS COULD NOT BE READ (#1247, RETRO-011).
//
// A failed list read answers `[]`, as the single read did before the lists
// were paged (#1202, #1138). That contract stays — a dozen callers rely on
// it, and a calendar missing some of its pages would be worse than an empty
// one. But on its own `[]` is indistinguishable from a club with no
// bookings, no activities and no matches, and staff were shown exactly that
// with nothing on the screen to say otherwise.
//
// So the paged reader records the outcome here, per list, and
// `ListLoadFailedBanner` says which lists are missing. It is recorded where
// the read happens rather than in one screen's state for the same reason as
// `serverReachability`: every caller of the reader gets it, including ones
// written after this.
//
// IT CLEARS ITSELF. The next successful read of a list clears that list, so
// the banner describes what is on the screen now, not what went wrong once.
//
// ONLY THE NEWEST READ OF A LIST DECIDES (#1264 review). Two reads of one list
// can overlap — the initial load and a refresh after a write — and the older
// one can finish last. Each read takes a ticket when it starts, and an
// outcome is applied only if no later read of that list has started since,
// so an old failure never overwrites a newer success, nor the other way.
//
// AND ONLY IN THE ORGANIZATION IT WAS READ FOR. A failure belongs to the club
// it was asked of. Moving the address every request carries
// (`scopeTransition.ts`) forgets every outcome and voids every ticket still
// out, so organization A's failure is not shown over B's lists — whether it
// was recorded before the switch or arrives after it.
// ─────────────────────────────────────────────────────────────────────

/** The response key each paged list is read under — one per list. */
export type ClubList = 'bookings' | 'activities' | 'singleMatches' | 'plannedMatches';

/** One read of one list, from the moment it starts. */
export interface ListRead {
  readonly list: ClubList;
  readonly seq: number;
}

const fresh = (): Record<ClubList, number> => ({
  bookings: 0,
  activities: 0,
  singleMatches: 0,
  plannedMatches: 0,
});

let latest = fresh();
let failed: ReadonlySet<ClubList> = new Set();
const listeners = new Set<(failed: ReadonlySet<ClubList>) => void>();

function publish(next: ReadonlySet<ClubList>) {
  failed = next;
  for (const l of listeners) l(failed);
}

/** A read of `list` is starting; every earlier one still out is now stale. */
export function startListRead(list: ClubList): ListRead {
  latest[list] += 1;
  return { list, seq: latest[list] };
}

/** The read finished. Applied only if it is still the newest read of its list. */
export function settleListRead(read: ListRead, ok: boolean): void {
  if (read.seq !== latest[read.list]) return;
  if (ok === !failed.has(read.list)) return;
  const next = new Set(failed);
  if (ok) next.delete(read.list);
  else next.add(read.list);
  publish(next);
}

/** The address moved: what was read for the previous one no longer applies. */
export function forgetListReads(): void {
  for (const list of Object.keys(latest) as ClubList[]) latest[list] += 1;
  if (failed.size > 0) publish(new Set());
}

export const failedListReads = (): ReadonlySet<ClubList> => failed;

export function subscribeListReads(fn: (failed: ReadonlySet<ClubList>) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Tests only — the module is a singleton by design, like the reachability signal. */
export function resetListReads(): void {
  latest = fresh();
  failed = new Set();
}
